export type DurationFormat = "full" | "compact" | "decimal";

const UNIT_SECONDS: Record<string, number> = { h: 3600, m: 60, s: 1 };

/**
 * Parses a typed duration into whole seconds, following Clockify's duration box:
 * - colon forms `h:mm` and `h:mm:ss` ("1:30" = 1h30m, "1:30:15"); minutes/seconds above 59 are rejected
 * - decimal hours with "." or "," ("1.5" = 1h30m)
 * - unit forms "1h 30m", "90m", "45s", "1.5h"
 * - bare digits: 1-2 digits are hours ("2" = 2h), 3-4 digits are h(h)mm ("130" = 1:30, "1245" = 12:45),
 *   5-6 digits are h(h)mmss ("13000" = 1:30:00)
 * Returns null for empty or unparseable text.
 */
export function parseDuration(text: string): number | null {
  const s = text.trim().toLowerCase();
  if (!s) return null;

  const colon = /^(\d+):(\d{1,2})(?::(\d{1,2}))?$/.exec(s);
  if (colon) {
    const h = Number(colon[1]);
    const m = Number(colon[2]);
    const sec = colon[3] === undefined ? 0 : Number(colon[3]);
    if (m > 59 || sec > 59) return null;
    return h * 3600 + m * 60 + sec;
  }

  if (/^\d*[.,]\d+$|^\d+[.,]$/.test(s)) {
    return Math.round(Number(s.replace(",", ".")) * 3600);
  }

  if (/^\d+$/.test(s)) {
    if (s.length <= 2) return Number(s) * 3600;
    if (s.length <= 4) {
      const m = Number(s.slice(-2));
      if (m > 59) return null;
      return Number(s.slice(0, -2)) * 3600 + m * 60;
    }
    if (s.length <= 6) {
      const sec = Number(s.slice(-2));
      const m = Number(s.slice(-4, -2));
      if (m > 59 || sec > 59) return null;
      return Number(s.slice(0, -4)) * 3600 + m * 60 + sec;
    }
    return null;
  }

  const unitRe = /(\d+(?:[.,]\d+)?)\s*([hms])/g;
  const compact = s.replace(/\s+/g, "");
  if (!/^(\d+(?:[.,]\d+)?[hms])+$/.test(compact)) return null;
  let total = 0;
  for (const match of s.matchAll(unitRe)) {
    total += Number((match[1] ?? "0").replace(",", ".")) * (UNIT_SECONDS[match[2] ?? "s"] ?? 0);
  }
  return Math.round(total);
}

/** `full` = Clockify's hh:mm:ss (`00:07:07`), `compact` = h:mm, `decimal` = hours with 2 decimals. */
export function formatDuration(seconds: number, format: DurationFormat = "full"): string {
  const total = Math.max(0, Math.round(seconds));
  if (format === "decimal") return (total / 3600).toFixed(2);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (format === "compact") return `${h}:${String(m).padStart(2, "0")}`;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
